import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

const ToastContext = createContext();

// Deduplication window in milliseconds (2 seconds)
const DEDUP_WINDOW = 2000;

/**
 * Toast Provider Component
 * Provides toast functionality to the entire app
 */
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  // Track recent toast messages to prevent duplicates
  const recentToastsRef = useRef(new Map());

  /**
   * Add a new toast with deduplication
   */
  const addToast = useCallback((message, type = 'info', duration = 5000) => {
    const now = Date.now();
    const toastKey = `${type}:${message}`;
    
    // Check if same message was shown recently
    const lastShown = recentToastsRef.current.get(toastKey);
    if (lastShown && (now - lastShown) < DEDUP_WINDOW) {
      // Duplicate detected within dedup window, skip it
      return null;
    }

    // Update the timestamp for this message
    recentToastsRef.current.set(toastKey, now);
    
    // Clean up old entries (older than dedup window)
    const cutoff = now - DEDUP_WINDOW;
    for (const [key, timestamp] of recentToastsRef.current.entries()) {
      if (timestamp < cutoff) {
        recentToastsRef.current.delete(key);
      }
    }

    const id = now + Math.random();
    const newToast = {
      id,
      message,
      type,
      duration
    };

    setToasts(prev => [...prev, newToast]);
    return id;
  }, []);

  /**
   * Remove a toast by ID
   */
  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  /**
   * Clear all toasts
   */
  const clearToasts = useCallback(() => {
    setToasts([]);
  }, []);

  /**
   * Convenience methods for different toast types
   */
  const showSuccess = useCallback((message, duration) => {
    return addToast(message, 'success', duration);
  }, [addToast]);

  const showError = useCallback((message, duration) => {
    return addToast(message, 'error', duration);
  }, [addToast]);

  const showWarning = useCallback((message, duration) => {
    return addToast(message, 'warning', duration);
  }, [addToast]);

  const showInfo = useCallback((message, duration) => {
    return addToast(message, 'info', duration);
  }, [addToast]);

  const value = {
    toasts,
    addToast,
    removeToast,
    clearToasts,
    showSuccess,
    showError,
    showWarning,
    showInfo
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
    </ToastContext.Provider>
  );
};

/**
 * Hook to use toast functionality
 */
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
